import React, {
	ReactNode,
	useRef,
	CSSProperties,
	useImperativeHandle,
	forwardRef,
	useEffect,
} from "react";
import { useVirtualizer, Virtualizer } from "@tanstack/react-virtual";
import { cn } from "../../utils/cn";
import { appTheme } from "../../theme/Theme";

interface VirtualizedTableLayoutProps {
	selectable?: boolean;
	virtualized?: boolean;
	itemCount?: number;
	estimatedRowHeight?: number;
	overscan?: number;
	renderHeader: () => ReactNode;
	renderRow: (index: number) => ReactNode;
	/** Extra attributes spread onto each row's <tr> (e.g. data-contact for the context menu). */
	getRowProps?: (index: number) => React.HTMLAttributes<HTMLTableRowElement>;
	onRowClick?: (index: number) => void;
	containerHeight?: string;
	className?: string;
	/** Called when the user scrolls near the end of the list (e.g. to fetch the next page). */
	onEndReached?: () => void;
	/** When true, renders a loading footer row beneath the last row. */
	isFetchingNextPage?: boolean;
}

export interface VirtualizedTableLayoutRef {
	scrollToIndex: (index: number, options?: { align?: "start" | "center" | "end" | "auto" }) => void;
	virtualizer: Virtualizer<HTMLDivElement, Element> | null;
}

const VirtualizedTableLayout = forwardRef<VirtualizedTableLayoutRef, VirtualizedTableLayoutProps>(
	(
		{
			selectable = true,
			virtualized = true,
			itemCount = 0,
			estimatedRowHeight = 50,
			overscan = 10,
			renderHeader,
			renderRow,
			getRowProps,
			onRowClick,
			containerHeight = "calc(100vh - 300px)",
			className,
			onEndReached,
			isFetchingNextPage = false,
		},
		ref
	) => {
		const parentRef = useRef<HTMLDivElement>(null);
		// Tracks the itemCount at which we last fired onEndReached, so we don't fire
		// repeatedly for the same end position until more items have actually loaded.
		const lastFiredItemCountRef = useRef<number>(-1);

		const rowVirtualizer = useVirtualizer({
			count: itemCount,
			getScrollElement: () => parentRef.current,
			estimateSize: () => estimatedRowHeight,
			overscan,
			measureElement:
				typeof window !== "undefined" && navigator.userAgent.indexOf("Firefox") === -1
					? (element) => element?.getBoundingClientRect().height
					: undefined,
		});

		// Expose virtualizer methods to parent via ref
		useImperativeHandle(ref, () => ({
			scrollToIndex: (index: number, options?: { align?: "start" | "center" | "end" | "auto" }) => {
				rowVirtualizer.scrollToIndex(index, options);
			},
			virtualizer: rowVirtualizer,
		}));

		const virtualItems = rowVirtualizer.getVirtualItems();

		// Fire onEndReached when the highest rendered virtual index nears the end of the list.
		// Debounced via lastFiredItemCountRef (one fire per itemCount) and guarded while
		// a fetch is already in flight, so we never spam the callback for the same end position.
		useEffect(() => {
			if (!onEndReached || itemCount === 0) {
				return;
			}

			const lastVirtualItem = virtualItems[virtualItems.length - 1];

			if (!lastVirtualItem) {
				return;
			}

			const threshold = Math.max(1, overscan);
			const reachedEnd = lastVirtualItem.index >= itemCount - 1 - threshold;

			if (reachedEnd && !isFetchingNextPage && lastFiredItemCountRef.current !== itemCount) {
				lastFiredItemCountRef.current = itemCount;
				onEndReached();
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [virtualItems, itemCount, overscan, isFetchingNextPage, onEndReached]);

		if (!virtualized) {
			// Fallback to non-virtualized rendering if disabled
			return (
				<div className={cn("w-full flex flex-col", className)} style={{ height: containerHeight }}>
					<table className="w-full border-collapse">
						{renderHeader()}
						<tbody>
							{Array.from({ length: itemCount }, (_, index) => (
								<tr key={index} {...getRowProps?.(index)}>
									{renderRow(index)}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			);
		}

		return (
			<div className={cn("w-full flex flex-col", className)} style={{ height: containerHeight }}>
				{/* Fixed Header */}
				<table className="w-full border-collapse" style={{ tableLayout: "fixed" }}>
					{renderHeader()}
				</table>

				{/* Scrollable Body */}
				<div ref={parentRef} className="overflow-auto flex-1 relative">
					<table className="w-full border-collapse relative" style={{ tableLayout: "fixed" }}>
						<tbody>
							<tr style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
								<td className="p-0 border-none" />
							</tr>
							{virtualItems.map((virtualRow) => {
								const isOdd = virtualRow.index % 2 === 1;
								const rowStyle: CSSProperties = {
									transform: `translateY(${virtualRow.start}px)`,
									backgroundColor: isOdd ? appTheme.colors.COLOR_OFF_WHITE : "transparent",
									minHeight: "3.125rem",
								};

								return (
									<tr
										key={String(virtualRow.key)}
										{...getRowProps?.(virtualRow.index)}
										data-index={virtualRow.index}
										ref={rowVirtualizer.measureElement}
										className={cn(
											"absolute top-0 left-0 w-full table table-fixed",
											selectable && "cursor-pointer",
											"transition-colors duration-150"
										)}
										style={rowStyle}
										onClick={onRowClick ? () => onRowClick(virtualRow.index) : undefined}
										onMouseEnter={(e) => {
											e.currentTarget.style.backgroundColor = appTheme.colors.COLOR_HOVER_ROW;
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.backgroundColor = isOdd
												? appTheme.colors.COLOR_OFF_WHITE
												: "transparent";
										}}
									>
										{renderRow(virtualRow.index)}
									</tr>
								);
							})}
							{isFetchingNextPage && (
								<tr
									className="absolute left-0 w-full table table-fixed"
									style={{ transform: `translateY(${rowVirtualizer.getTotalSize()}px)` }}
								>
									<td colSpan={1000} className="py-3 text-center text-sm text-gray-500 border-none">
										<span className="inline-flex items-center gap-2">
											<span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
											Laddar fler…
										</span>
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</div>
		);
	}
);

VirtualizedTableLayout.displayName = "VirtualizedTableLayout";

export default VirtualizedTableLayout;
